import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect, useRef } from "react";
import { useTenant } from "../lib/tenant-context";
import { resolveMarketplaceLogos } from "../lib/marketplace-logos";
import { useAuth } from "../lib/auth-context";
import { MarketIntelligenceSetup } from "../components/MarketIntelligenceSetup";
import { FeatureGate } from "../components/FeatureGate";
import { tenantFetch } from "../lib/api-client";
import { Earth, ArrowRightUp, ChartSquare, Magnifer, Filter, Buildings2 } from "@solar-icons/react";
import {
  EmptyState,
  Button,
  Badge,
  Card,
  CardContent,
  Alert,
  AlertTitle,
  AlertDescription,
  Dialog,
  DialogContent,
  Toggle,
  ToggleGroup,
  ToggleGroupItem,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  AspectRatio,
  Skeleton,
  Spinner,
  Separator,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetBody,
} from "@pentatonic-ltd/ui";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from "recharts";

interface IntelligenceData {
  brand_id: string;
  brand_name: string;
  period_days: number;
  total_active_listings: number;
  total_sold_period: number;
  estimated_annual_gmv: number;
  avg_sale_price: number;
  sell_through_rate: number;
  avg_rrp_percentage: number | null;
  platform_breakdown: Record<string, number>;
  condition_breakdown: Record<string, number>;
  category_breakdown: Record<string, number>;
  daily_trend: {
    date: string;
    active_listings: number;
    sales: number;
    avg_price: number | null;
  }[];
}

interface ListingItem {
  id: string;
  title: string;
  price: number | null;
  price_gbp: number | null;
  currency: string;
  platform: string;
  condition: string;
  listing_type: string;
  listing_url: string;
  image_urls: string[];
  category: string;
  size: string;
  first_seen_at: string;
  last_seen_at: string;
}

interface ListingsResponse {
  items: ListingItem[];
  total: number;
  page: number;
  pages: number;
}

// ── Retailer / Catalog types ──────────────────────────────────────────────────

interface CatalogIntelligence {
  catalog: {
    total_products: number;
    by_category: Record<string, number>;
    by_availability: Record<string, number>;
  };
  competitive_coverage: {
    competitor_products_total: number;
    matched_to_catalog: number;
    match_coverage_pct: number;
    note: string;
    by_competitor: { name: string; domain: string; products_scraped: number; products_matched: number }[];
  };
  price_position: {
    products_analysed: number;
    catalog_cheapest: number;
    catalog_competitive: number;
    catalog_most_expensive: number;
    avg_gap_pct: number;
  };
}

interface PricePosition {
  is_cheapest: boolean;
  gap_pct: number;
  cheapest_competitor_name: string;
  cheapest_competitor_price: number;
}

interface CatalogProduct {
  id: number;
  name: string;
  brand: string;
  price: number;
  currency: string;
  category: string;
  availability: string;
  url: string;
  image_url: string;
  sku: string;
  price_position: PricePosition | null;
}

interface CatalogProductsResponse {
  items: CatalogProduct[];
  total: number;
  page: number;
  pages: number;
}

interface CatalogAlert {
  product_id: number;
  product_name: string;
  catalog_price: number;
  catalog_url: string;
  cheapest_competitor_name: string;
  cheapest_competitor_domain: string;
  cheapest_price: number;
  cheapest_url: string;
  price_gap_pct: number;
  price_scraped_at: string | null;
}

interface CompetitorPrice {
  competitor_name: string;
  competitor_domain: string;
  product_name: string;
  price: number;
  currency: string;
  availability: string;
  url: string;
  price_gap_pct: number;
}

interface CompareData {
  product: { id: number; name: string; price: number; currency: string; url: string };
  matched: boolean;
  competitor_prices: CompetitorPrice[];
}

interface CatalogCompetitor {
  domain: string;
  name: string;
  last_scraped: string;
  products_scraped: number;
  products_matched: number;
}

export const Route = createFileRoute("/market-intelligence")({
  component: MarketIntelligenceDashboard,
});

const AVAILABILITY_LABELS: Record<string, string> = {
  in_stock: "In stock",
  out_of_stock: "Unavailable",
  pre_order: "Pre-order",
  low_stock: "Low stock",
};

const SCANNING_VERBS = ["Rummaging through", "Rifling through", "Fossicking through", "Sifting through", "Trawling through", "Foraging through"];


function MarketIntelligenceDashboard() {
  const { currentTenant } = useTenant();
  const { user, isLoading: authLoading } = useAuth();
  const isFreePlan = user?.tenantPlan === "free";

  const businessType = user?.businessType ?? null;

  const scanningPhrases = resolveMarketplaceLogos(user?.marketplaces)
    .map((m, i) => `${SCANNING_VERBS[i % SCANNING_VERBS.length]} ${m.name}`);

  const [data, setData] = useState<IntelligenceData | null>(null);
  const [listings, setListings] = useState<ListingsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [brandNotFound, setBrandNotFound] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [scanning, setScanning] = useState(false);
  const scanPollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadIntelligenceRef = useRef<(() => Promise<void>) | null>(null);
  const scanAttemptsRef = useRef(0);
  const MAX_SCAN_ATTEMPTS = 36; // 3 min at 5s intervals
  const [period, setPeriod] = useState<"7d" | "30d" | "90d">("7d");
  const [periodGateOpen, setPeriodGateOpen] = useState(false);
  const [slideIndex, setSlideIndex] = useState(0);
  const [listingsPage, setListingsPage] = useState(1);
  const [listingsPlatform, setListingsPlatform] = useState("");
  const [listingsSearch, setListingsSearch] = useState("");
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [phraseVisible, setPhraseVisible] = useState(true);
  const [listingsSort, setListingsSort] = useState("newest");

  // For free-plan tenants, gate auto-fetch behind an explicit opt-in.
  // setupChecked starts false — we wait for auth to resolve before deciding.
  const [setupChecked, setSetupChecked] = useState(false);
  const [marketRequested, setMarketRequested] = useState(false);


  useEffect(() => {
    if (authLoading) return;

    if (!isFreePlan) {
      setSetupChecked(true);
      return;
    }

    tenantFetch("/api/v1/market-intelligence/setup")
      .then((r) => r.json())
      .then((r) => {
        setMarketRequested(!!r.requested);
        setSetupChecked(true);
      })
      .catch(() => setSetupChecked(true));
  }, [currentTenant.id, authLoading, isFreePlan]);

  useEffect(() => {
    if (!setupChecked) return;
    if (businessType === "retailer") {
      setLoading(false);
      setScanning(false);
      return;
    }
    if (isFreePlan && !marketRequested) {
      setLoading(false);
      return;
    }
    loadIntelligence();
  }, [currentTenant.id, period, setupChecked, marketRequested, businessType]);

  useEffect(() => {
    if (data) {
      loadListings();
    }
  }, [
    data,
    currentTenant.id,
    listingsPage,
    listingsPlatform,
    listingsSort,
    listingsSearch,
  ]);

  const loadIntelligence = async () => {
    if (!scanning) setLoading(true);
    setBrandNotFound(false);
    setFetchError(false);
    try {
      const res = await tenantFetch(
        `/api/v1/market-intelligence?period=${period}`,
      );
      if (res.ok) {
        const result = await res.json();
        if (result.data?.status === "scraping") {
          // Scraper job kicked off — poll until ready
          setScanning(true);
          setLoading(false);
          scanAttemptsRef.current += 1;
          // Use ref so the timeout always calls the latest closure (avoids stale scanning=false)
          scanPollRef.current = setTimeout(() => loadIntelligenceRef.current?.(), 3000);
          return;
        }
        scanAttemptsRef.current = 0;
        setScanning(false);
        setData(result.data);
      } else if (res.status === 404) {
        setScanning(false);
        setBrandNotFound(true);
        setData(null);
      } else if (scanning) {
        // Error during an active scan — brand may not be registered in DGX yet, keep polling
        scanAttemptsRef.current += 1;
        if (scanAttemptsRef.current < MAX_SCAN_ATTEMPTS) {
          scanPollRef.current = setTimeout(() => loadIntelligenceRef.current?.(), 5000);
          return;
        }
        // Polling timed out — show pending state rather than a generic error
        setScanning(false);
        setBrandNotFound(true);
      } else if (isFreePlan) {
        setFetchError(true);
      } else {
        setData(getDemoIntelligence(currentTenant.id, period));
      }
    } catch {
      if (scanning) {
        // Network error during polling — keep retrying
        scanAttemptsRef.current += 1;
        if (scanAttemptsRef.current < MAX_SCAN_ATTEMPTS) {
          scanPollRef.current = setTimeout(() => loadIntelligenceRef.current?.(), 5000);
          return;
        }
        setScanning(false);
        setBrandNotFound(true);
      } else if (isFreePlan) {
        setFetchError(true);
      } else {
        setData(getDemoIntelligence(currentTenant.id, period));
      }
    } finally {
      setLoading(false);
    }
  };
  loadIntelligenceRef.current = loadIntelligence;

  // Clear poll timer on unmount
  useEffect(() => () => {
    if (scanPollRef.current) clearTimeout(scanPollRef.current);
  }, []);

  // Cycle scanning phrases with fade transition
  useEffect(() => {
    if (!scanning) return;
    const interval = setInterval(() => {
      setPhraseVisible(false);
      setTimeout(() => {
        setPhraseIndex(i => (i + 1) % scanningPhrases.length);
        setPhraseVisible(true);
      }, 300);
    }, 2500);
    return () => clearInterval(interval);
  }, [scanning]);

  // Rotate upgrade dialog slides when open
  useEffect(() => {
    if (!periodGateOpen) return;
    setSlideIndex(0);
    const timer = setInterval(() => {
      setSlideIndex(i => (i + 1) % 3);
    }, 5000);
    return () => clearInterval(timer);
  }, [periodGateOpen]);

  const loadListings = async () => {
    try {
      const params = new URLSearchParams({
        page: String(listingsPage),
        limit: "24",
        sort: listingsSort,
      });
      if (listingsPlatform) params.set("platform", listingsPlatform);
      if (listingsSearch) params.set("q", listingsSearch);

      const res = await tenantFetch(
        `/api/v1/market-intelligence/listings?${params}`,
      );
      if (res.ok) {
        const result = await res.json();
        setListings(result.data);
      } else if (!isFreePlan) {
        setListings(getDemoListings(currentTenant.id));
      }
    } catch {
      if (!isFreePlan) setListings(getDemoListings(currentTenant.id));
    }
  };

  // Render nothing until setup resolves — prevents skeleton flash before the real state is known
  if (!setupChecked) return null;

  // Show skeleton only while actively loading intelligence data
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton width={224} height={32} />
            <Skeleton width={288} height={16} />
          </div>
          <Skeleton width={208} height={36} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => <Skeleton key={i} width="100%" height={120} />)}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} width="100%" height={96} />)}
        </div>
        <Skeleton width="100%" height={288} />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => <Skeleton key={i} width="100%" height={256} />)}
        </div>
        <Skeleton width={192} height={32} />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => <Skeleton key={i} width="100%" height={256} />)}
        </div>
      </div>
    );
  }

  if (scanning) {
    return (
      <div className="flex flex-col flex-1">
        {/* Centred content */}
        <div className="flex-1 flex flex-col items-center justify-center gap-6 max-w-lg mx-auto w-full px-10 text-center">
          <Spinner />
          <div className="flex flex-col gap-4">
            <p
              className="text-[29px] font-light text-foreground leading-[1.1] transition-opacity duration-300 whitespace-nowrap"
              style={{ opacity: phraseVisible ? 1 : 0 }}
            >
              {scanningPhrases[phraseIndex]}
            </p>
            <p className="text-[18px] font-light leading-[1.4] text-grey-medium">
              This could take a little while. Check back soon
            </p>
          </div>
        </div>

        {/* Logos pinned to bottom */}
        <div className="flex flex-col gap-6 items-center w-full px-10 pb-12">
          <p className="text-[18px] font-light text-foreground text-center leading-[1.4]">
            Obtaining insights from
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-4 items-center justify-center w-full">
            {resolveMarketplaceLogos(user?.marketplaces).map(logo => (
              <img
                key={logo.name}
                src={logo.src}
                alt={logo.name}
                width={logo.w}
                height={logo.h}
                className="object-contain"
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Route to retailer dashboard for retailer business type
  if (businessType === "retailer") {
    return <RetailerDashboard />;
  }

  // Free-plan users must opt in before we fetch real data
  if (isFreePlan && !marketRequested) {
    return (
      <MarketIntelligenceSetup
        onActivated={() => {
          scanAttemptsRef.current = 0;
          setScanning(true);
          setMarketRequested(true);
        }}
        marketplaces={user?.marketplaces}
      />
    );
  }

  if (fetchError) {
    return (
      <div className="space-y-6">
        <div className="space-y-1">
          <h1 className="text-[29px] font-light text-foreground">
            Market Intelligence
          </h1>
          <p className="text-sm text-muted-foreground">
            Resale market monitoring powered by real marketplace data
          </p>
        </div>
        <div className="max-w-lg space-y-4">
          <Alert variant="error">
            <AlertTitle>Market data unavailable</AlertTitle>
            <AlertDescription>
              There was a problem fetching your market intelligence data. Check your connection and try again.
            </AlertDescription>
          </Alert>
          <Button variant="primary" onClick={loadIntelligence}>
            Try again
          </Button>
        </div>
      </div>
    );
  }

  if (brandNotFound) {
    const isUnknownType = businessType === "unknown";
    return (
      <div className="space-y-6">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <h1 className="text-[29px] font-light text-foreground">
              Market Intelligence
            </h1>
            <Badge variant="warning">Pending</Badge>
          </div>
          <p className="text-sm text-muted-foreground">
            Resale market monitoring powered by real marketplace data
          </p>
        </div>
        <div className="max-w-lg">
          <Card variant="outlined" padding="none">
            <CardContent>
              <EmptyState
                icon={
                  <span className="bg-muted p-4 inline-flex items-center justify-center">
                    <Earth size={32} weight="LineDuotone" className="text-muted-foreground" />
                  </span>
                }
                title={isUnknownType ? "Business type not recognised" : "Monitoring not yet active"}
                description={
                  isUnknownType
                    ? "We weren't able to classify your business type, so we can't set up accurate market monitoring. Contact support and we'll get this sorted."
                    : "Your brand has been queued for monitoring. Pentatonic will set up tracking across eBay, Vinted, Depop, Vestiaire Collective, and TheRealReal — we'll notify you when it's ready."
                }
                action={
                  <Button variant="primary" disabled={isUnknownType} asChild={!isUnknownType}>
                    {isUnknownType ? (
                      "Contact support"
                    ) : (
                      <a href="mailto:support@pentatonic.com?subject=Enable%20Market%20Intelligence">
                        Contact support
                      </a>
                    )}
                  </Button>
                }
              />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="space-y-6">
        <div className="space-y-1">
          <h1 className="text-[29px] font-light text-foreground">
            Market Intelligence
          </h1>
          <p className="text-sm text-muted-foreground">
            Resale market monitoring powered by real marketplace data
          </p>
        </div>
        <div className="max-w-lg space-y-4">
          <Alert variant="error">
            <AlertTitle>Market data unavailable</AlertTitle>
            <AlertDescription>
              Something went wrong loading your market data. Please try again.
            </AlertDescription>
          </Alert>
          <Button variant="primary" onClick={loadIntelligence}>
            Try again
          </Button>
        </div>
      </div>
    );
  }

  // Derive annual transactions from 7-day observed sold data
  const annualTransactions = Math.round((data.total_sold_period / 7) * 365);
  const annualGmv = Math.round(annualTransactions * (data.avg_sale_price ?? 0));
  // Trove Brand Resale Index: 50-65% of take-back participants are new to the brand
  const returningCustomers = Math.round(annualTransactions * 0.15 * 0.45);
  const newCustomers = Math.round(annualTransactions * 0.15 * 0.55);

  // Design-system compliant chart palette (no mint, no blue, no tenant color)
  const CHART_COLORS = ["#171717", "#FF4C00", "#5BC48B", "#FFB788", "#9EA8FF"];

  const platformData = Object.entries(data.platform_breakdown)
    .map(([name, count]) => ({
      name: name.charAt(0).toUpperCase() + name.slice(1),
      count,
    }))
    .sort((a, b) => b.count - a.count);

  const conditionData = Object.entries(data.condition_breakdown)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);

  const categoryData = Object.entries(data.category_breakdown)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);

  const totalPlatformListings = platformData.reduce((s, d) => s + d.count, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-[29px] font-light text-foreground leading-[1.1]">
              Market Intelligence
            </h1>
            <p className="text-base mt-1 text-muted-foreground">
              How {data.brand_name || currentTenant.name} appears across resale marketplaces
            </p>
          </div>
        </div>
      </div>

      {/* Layer 1 — Opportunity */}
      <div className="space-y-4">
        <p className="font-sans font-light text-[29px] leading-[1.1] text-foreground">
          Annual Opportunity
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <OpportunityCard
            label="Takeback Opportunity"
            value={`£${formatCompact(calculateTakebackOpportunity(annualTransactions, data.avg_sale_price ?? 0))}`}
            description="Additional revenue you could be bringing in by running a take-back programme."
          />
          <OpportunityCard
            label="Returning Customers"
            value={returningCustomers.toLocaleString()}
            description="Existing customers you could bring back and engage with online or in-store."
          />
          <OpportunityCard
            label="New Customers"
            value={newCustomers.toLocaleString()}
            description="New customers you could acquire through a take-back programme."
          />
        </div>
      </div>

      {/* Layer 2 — Market Evidence */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="font-sans font-light text-[29px] leading-[1.1] text-foreground">
            Explore the Data
          </p>
          <ToggleGroup
            type="single"
            value={period}
            onValueChange={(v: string) => {
              if (!v) return;
              if (isFreePlan && (v === "30d" || v === "90d")) {
                setPeriodGateOpen(true);
                return;
              }
              setPeriod(v as "7d" | "30d" | "90d");
            }}
          >
            <ToggleGroupItem value="7d">7 Days</ToggleGroupItem>
            <ToggleGroupItem value="30d">30 Days</ToggleGroupItem>
            <ToggleGroupItem value="90d">90 Days</ToggleGroupItem>
          </ToggleGroup>

          <Dialog open={periodGateOpen} onOpenChange={setPeriodGateOpen}>
            <DialogContent size="xl">
              {(() => {
                const takebackValue = `£${formatCompact(calculateTakebackOpportunity(annualTransactions, data?.avg_sale_price ?? 0))}`;
                const slides = [
                  {
                    label: "Potential Revenue",
                    value: takebackValue,
                    description: "Additional revenue you could be bringing in by running a take-back programme.",
                    color: "#9ea8ff",
                  },
                  {
                    label: "Returning Customers",
                    value: returningCustomers.toLocaleString(),
                    description: "Existing customers you could bring back and engage with online or in-store.",
                    color: "#6ee7b7",
                  },
                  {
                    label: "New Customers",
                    value: newCustomers.toLocaleString(),
                    description: "New customers you could acquire through a take-back programme.",
                    color: "#fca5a5",
                  },
                ];
                const slide = slides[slideIndex];
                const match = slide.value.match(/^(£?)(.+?)([KMB%]?)$/);
                const prefix = match?.[1] ?? "";
                const main = match?.[2] ?? slide.value;
                const suffix = match?.[3] ?? "";
                return (
              <>
              <style>{`
                @keyframes fillBar { from { width: 0% } to { width: 100% } }
                @keyframes fadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
                [data-pui="DialogContent"] > button { display: none; }
              `}</style>
              <div className="flex -m-6 lg:-m-8">
                {/* Left panel — upgrade prompt */}
                <div className="flex-1 self-stretch flex flex-col justify-between gap-4 p-6">
                  <div className="flex flex-col gap-4">
                    <p className="text-[29px] font-light leading-[1.4] text-foreground">
                      Ready to take control of your post-sale market?
                    </p>
                    <p className="text-[18px] font-light leading-[1.4] text-grey-medium">
                      Explore a deeper view of your brand's secondhand market — including trends, seasonal patterns, and more accurate opportunity sizing.
                    </p>
                  </div>
                  <button
                    className="self-start bg-[#171717] text-white font-mono text-[18px] font-light uppercase tracking-[0.02em] px-6 py-3 rounded-full"
                    onClick={() => {
                      window.location.href = "mailto:hello@pentatonic.com?subject=Market%20Intelligence%20Extended%20History";
                    }}
                  >
                    speak to us
                  </button>
                </div>
                {/* Right panel — rotating opportunity card */}
                <div className="bg-[#171717] flex-1 self-stretch flex flex-col gap-6 p-6">
                  {/* Tag + progress bars */}
                  <div className="flex flex-col gap-3">
                    <p className="font-mono text-[14px] font-light uppercase tracking-[0.02em] text-[#a9a9a9]">
                      The Opportunity
                    </p>
                    <div className="flex gap-3 h-[2px]">
                      {slides.map((_, i) => (
                        <div key={i} className="flex-1 relative h-[2px]">
                          <div className="absolute inset-0 bg-white/10" />
                          {i < slideIndex && (
                            <div className="absolute inset-y-0 left-0 bg-white/40 w-full" />
                          )}
                          {i === slideIndex && (
                            <div
                              key={`bar-${slideIndex}`}
                              className="absolute inset-y-0 left-0 bg-white"
                              style={{ animation: "fillBar 5s linear forwards" }}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Slide title */}
                  <p
                    key={`label-${slideIndex}`}
                    className="text-[29px] font-light leading-[1.4] text-white"
                    style={{ animation: "fadeUp 0.4s ease 0.05s forwards", opacity: 0 }}
                  >
                    {slide.label}
                  </p>
                  {/* Figure */}
                  <p
                    key={`fig-${slideIndex}`}
                    className="leading-none"
                    style={{ animation: "fadeUp 0.5s ease 0.1s forwards", opacity: 0 }}
                  >
                    <span className="text-[92px] font-extralight leading-none" style={{ color: slide.color }}>
                      {prefix}{main}
                    </span>
                    {suffix && (
                      <span className="text-[64px] font-extralight leading-none" style={{ color: slide.color }}>{suffix}</span>
                    )}
                  </p>
                  {/* Description */}
                  <p
                    key={`desc-${slideIndex}`}
                    className="text-[18px] font-light leading-[1.4] text-[#a9a9a9]"
                    style={{ animation: "fadeUp 0.5s ease 0.25s forwards", opacity: 0 }}
                  >
                    {slide.description}
                  </p>
                </div>
              </div>
              </>
                );
              })()}
            </DialogContent>
          </Dialog>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <MetricCard
            label="Annual Resale GMV"
            value={`£${formatCompact(annualGmv)}`}
            subtext="Est. total secondhand market value"
          />
          <MetricCard
            label="Active Listings"
            value={data.total_active_listings.toLocaleString()}
            subtext="Across UK platforms"
          />
          <MetricCard
            label="Avg. Resale Price"
            value={data.avg_sale_price != null ? `£${data.avg_sale_price.toFixed(0)}` : "—"}
            subtext="Mean listing price"
          />
          <MetricCard
            label="Sell-Through Rate"
            value={data.sell_through_rate != null ? `${data.sell_through_rate.toFixed(0)}%` : "—"}
            subtext="Sold within 30 days"
          />
        </div>
      </div>

      {/* Trend Chart */}
      <div className="bg-white border border-grey p-6">
        {/* Header: title + legend */}
        <div className="flex items-center justify-between mb-6">
          <p className="font-sans font-light text-[29px] leading-[1.1] text-foreground">
            Daily Activity
          </p>
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <div className="w-3 h-[1px] bg-black" />
              <span className="font-mono text-sm uppercase tracking-[0.05em] text-grey-medium">
                Active Listings
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-[1px] bg-coral" />
              <span className="font-mono text-sm uppercase tracking-[0.05em] text-grey-medium">
                Sales
              </span>
            </div>
          </div>
        </div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%" minWidth={100} minHeight={100}>
            <AreaChart data={data.daily_trend} margin={{ top: 4, right: 0, left: -40, bottom: 0 }}>
              <defs>
                <linearGradient id="activeGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#171717" stopOpacity={0.08} />
                  <stop offset="95%" stopColor="#171717" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="salesGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#FF4C00" stopOpacity={0.12} />
                  <stop offset="95%" stopColor="#FF4C00" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="date"
                axisLine={false}
                tickLine={false}
                tick={{
                  fontSize: 14,
                  fontFamily: "Atkinson Hyperlegible Mono",
                  fontWeight: 200,
                  fill: "#171717",
                }}
                tickFormatter={(v) => {
                  const d = new Date(v);
                  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" }).toUpperCase();
                }}
                interval={Math.ceil(data.daily_trend.length / 8) - 1}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{
                  fontSize: 14,
                  fontFamily: "Atkinson Hyperlegible Mono",
                  fontWeight: 200,
                  fill: "#171717",
                }}
                width={40}
                mirror
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#ffffff",
                  border: "1px solid #f1f1f1",
                  borderRadius: 0,
                  padding: "12px 16px",
                  fontFamily: "Atkinson Hyperlegible Next",
                  fontSize: 14,
                  fontWeight: 300,
                }}
                labelStyle={{
                  color: "#A9A9A9",
                  fontSize: 14,
                  fontFamily: "Atkinson Hyperlegible Mono",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  marginBottom: 4,
                }}
                itemStyle={{ color: "#171717" }}
                labelFormatter={(v) => {
                  const d = new Date(v);
                  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }).toUpperCase();
                }}
                cursor={{ stroke: "#A9A9A9", strokeWidth: 1, strokeDasharray: "4 4" }}
              />
              <Area
                type="monotone"
                dataKey="active_listings"
                stroke="#171717"
                strokeWidth={1.5}
                fill="url(#activeGrad)"
                name="Active Listings"
                dot={false}
                activeDot={{ r: 3, fill: "#171717", strokeWidth: 0 }}
              />
              <Area
                type="monotone"
                dataKey="sales"
                stroke="#FF4C00"
                strokeWidth={1.5}
                fill="url(#salesGrad)"
                name="Sales"
                dot={false}
                activeDot={{ r: 3, fill: "#FF4C00", strokeWidth: 0 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Breakdowns */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Platform Breakdown */}
        <div className="bg-white border border-grey p-6">
          <p className="font-sans font-light text-[29px] leading-[1.1] text-foreground mb-6">
            By Platform
          </p>
          <div className="h-44">
            <ResponsiveContainer width="100%" height="100%" minWidth={100} minHeight={100}>
              <PieChart>
                <Pie
                  data={platformData}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={68}
                  paddingAngle={2}
                  dataKey="count"
                  strokeWidth={0}
                >
                  {platformData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value) => [Number(value).toLocaleString(), "Listings"]}
                  contentStyle={{
                    backgroundColor: "#ffffff",
                    border: "1px solid #f1f1f1",
                    borderRadius: 0,
                    padding: "10px 14px",
                    fontFamily: "Atkinson Hyperlegible Next",
                    fontSize: 14,
                    fontWeight: 300,
                  }}
                  labelStyle={{
                    fontFamily: "Atkinson Hyperlegible Mono",
                    fontSize: 14,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    color: "#A9A9A9",
                  }}
                  itemStyle={{ color: "#171717" }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-2 mt-4">
            {platformData.map((p, i) => (
              <div key={p.name} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 shrink-0" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                  <span className="font-mono text-sm uppercase tracking-[0.05em] text-foreground">{p.name}</span>
                </div>
                <span className="font-mono text-sm uppercase tracking-[0.05em] text-foreground">
                  {totalPlatformListings > 0 ? Math.round((p.count / totalPlatformListings) * 100) : 0}%
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Condition Breakdown */}
        <div className="bg-white border border-grey p-6">
          <p className="font-sans font-light text-[29px] leading-[1.1] text-foreground mb-6">
            By Condition
          </p>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%" minWidth={100} minHeight={100}>
              <BarChart data={conditionData} layout="vertical" margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                <XAxis
                  type="number"
                  axisLine={false}
                  tickLine={false}
                  tick={{
                    fontSize: 14,
                    fontFamily: "Atkinson Hyperlegible Mono",
                    fontWeight: 200,
                    fill: "#171717",
                  }}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{
                    fontSize: 14,
                    fontFamily: "Atkinson Hyperlegible Mono",
                    fontWeight: 200,
                    fill: "#171717",
                    textAnchor: "end",
                  }}
                  width={90}
                  tickFormatter={(v) => String(v).toUpperCase()}
                />
                <Tooltip
                  formatter={(value) => [Number(value).toLocaleString(), "Listings"]}
                  contentStyle={{
                    backgroundColor: "#ffffff",
                    border: "1px solid #f1f1f1",
                    borderRadius: 0,
                    padding: "10px 14px",
                    fontFamily: "Atkinson Hyperlegible Next",
                    fontSize: 14,
                    fontWeight: 300,
                  }}
                  labelStyle={{
                    fontFamily: "Atkinson Hyperlegible Mono",
                    fontSize: 14,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    color: "#A9A9A9",
                  }}
                  itemStyle={{ color: "#171717" }}
                  cursor={{ fill: "#f1f1f1" }}
                />
                <Bar dataKey="count" fill="#171717" radius={0} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Category Breakdown */}
        <div className="bg-white border border-grey p-6">
          <p className="font-sans font-light text-[29px] leading-[1.1] text-foreground mb-6">
            Top Categories
          </p>
          <div className="space-y-4">
            {categoryData.slice(0, 8).map((cat) => {
              const maxCount = categoryData[0]?.count || 1;
              return (
                <div key={cat.name}>
                  <div className="flex justify-between mb-1">
                    <span className="font-mono text-sm uppercase tracking-[0.05em] text-foreground">
                      {cat.name || "Uncategorized"}
                    </span>
                    <span className="font-mono text-sm uppercase tracking-[0.05em] text-foreground">
                      {cat.count.toLocaleString()}
                    </span>
                  </div>
                  <div className="w-full h-[2px] bg-grey">
                    <div
                      className="h-[2px] bg-black"
                      style={{ width: `${(cat.count / maxCount) * 100}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Listings — Product Grid */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="font-sans font-light text-[29px] leading-[1.1] text-foreground">Listings</p>
            {listings && (
              <p className="font-mono text-sm uppercase tracking-[0.05em] text-grey-medium mt-1">
                {listings.total.toLocaleString()} results
              </p>
            )}
          </div>
          <div className="flex gap-4 items-center">
            <div className="w-64">
              <Input
                type="text"
                placeholder="Search listings..."
                value={listingsSearch}
                onChange={(e) => {
                  setListingsSearch(e.target.value);
                  setListingsPage(1);
                }}
              />
            </div>
            <div className="w-44">
              <Select
                value={listingsPlatform || "_all"}
                onValueChange={(v) => {
                  setListingsPlatform(v === "_all" ? "" : v);
                  setListingsPage(1);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Platforms" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">All Platforms</SelectItem>
                  {platformData.map((p) => (
                    <SelectItem key={p.name} value={p.name.toLowerCase()}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <Select
                value={listingsSort}
                onValueChange={(v) => {
                  setListingsSort(v);
                  setListingsPage(1);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Newest</SelectItem>
                  <SelectItem value="oldest">Oldest</SelectItem>
                  <SelectItem value="price_asc">Price: Low to High</SelectItem>
                  <SelectItem value="price_desc">Price: High to Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {listings && listings.items.length > 0 ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {listings.items.map((item, i) => {
                const isSold = item.listing_type === "sold";

                return (
                  <a
                    key={`${item.id}-${i}`}
                    href={item.listing_url || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex flex-col bg-white border border-[#f1f1f1]"
                  >
                      {/* Image */}
                      <div className="relative overflow-hidden bg-[#d9d9d9]">
                        <AspectRatio ratio={1}>
                          {item.image_urls?.[0] ? (
                            <img
                              src={item.image_urls[0].replace("s-l225", "s-l500")}
                              alt={item.title}
                              className={`w-full h-full object-cover transition-opacity duration-100 group-hover:opacity-90 ${isSold ? "opacity-50" : ""}`}
                              onError={(e) => {
                                const el = e.target as HTMLImageElement;
                                el.onerror = null;
                                el.style.display = "none";
                              }}
                            />
                          ) : (
                            <div className="w-full h-full bg-[#d9d9d9]" />
                          )}
                        </AspectRatio>
                        {isSold && (
                          <div className="absolute top-2 right-2">
                            <Badge variant="default" size="sm">Sold</Badge>
                          </div>
                        )}
                      </div>
                      {/* Text content */}
                      <div className="p-6 flex-1 flex flex-col justify-between gap-2">
                        <div className="flex flex-col gap-1">
                          <p className="font-sans font-light text-[18px] leading-[1.4] text-foreground overflow-hidden" style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }} title={item.title}>
                            {item.title}
                          </p>
                          <p className="font-mono font-light text-[14px] leading-[1.1] uppercase tracking-[0.02em] text-grey-medium">
                            {[item.condition, item.first_seen_at ? formatDate(item.first_seen_at) : null].filter(Boolean).join(" • ")}
                          </p>
                        </div>
                        <div className="flex flex-col gap-2">
                          <p className={`font-sans font-light text-[29px] leading-[1.4] ${isSold ? "text-grey-medium" : "text-foreground"}`}>
                            {item.price != null ? formatCcy(item.price, item.currency) : "—"}
                          </p>
                          <div className="inline-flex">
                            <span className="bg-[#171717] px-2 py-1 font-mono font-light text-sm leading-[1.1] uppercase tracking-[0.02em] text-white">
                              {item.platform}
                            </span>
                          </div>
                        </div>
                      </div>
                  </a>
                );
              })}
            </div>

            {/* Pagination */}
            {listings.pages > 1 && (
              <div className="mt-6 flex flex-col gap-4">
                <Separator />
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        onClick={() => setListingsPage(Math.max(1, listingsPage - 1))}
                        aria-disabled={listingsPage <= 1}
                      />
                    </PaginationItem>

                    {/* Page number links with ellipsis for large ranges */}
                    {(() => {
                      const total = listings.pages;
                      const current = listingsPage;
                      const pages: (number | "ellipsis")[] = [];

                      if (total <= 7) {
                        for (let i = 1; i <= total; i++) pages.push(i);
                      } else {
                        pages.push(1);
                        if (current > 4) pages.push("ellipsis");
                        const start = Math.max(2, current - 1);
                        const end = Math.min(total - 1, current + 1);
                        for (let i = start; i <= end; i++) pages.push(i);
                        if (current < total - 3) pages.push("ellipsis");
                        pages.push(total);
                      }

                      return pages.map((p, i) =>
                        p === "ellipsis" ? (
                          <PaginationItem key={`ellipsis-${i}`}>
                            <PaginationEllipsis />
                          </PaginationItem>
                        ) : (
                          <PaginationItem key={p}>
                            <PaginationLink
                              isActive={p === current}
                              onClick={() => setListingsPage(p)}
                            >
                              {p}
                            </PaginationLink>
                          </PaginationItem>
                        )
                      );
                    })()}

                    <PaginationItem>
                      <PaginationNext
                        onClick={() => setListingsPage(Math.min(listings.pages, listingsPage + 1))}
                        aria-disabled={listingsPage >= listings.pages}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            )}
          </>
        ) : (
          <Card variant="outlined" padding="none">
            <CardContent>
              <EmptyState
                icon={
                  <span className="bg-muted p-4 inline-flex items-center justify-center">
                    <Earth size={32} weight="LineDuotone" className="text-muted-foreground" />
                  </span>
                }
                title="No listings found"
                description="Try adjusting your filters or search terms."
              />
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

// ── RetailerDashboard ─────────────────────────────────────────────────────────

function RetailerDashboard() {
  const { currentTenant } = useTenant();

  const [intelligence, setIntelligence] = useState<CatalogIntelligence | null>(null);
  const [products, setProducts] = useState<CatalogProductsResponse | null>(null);
  const [alerts, setAlerts] = useState<CatalogAlert[]>([]);
  const [competitors, setCompetitors] = useState<CatalogCompetitor[]>([]);
  const [loading, setLoading] = useState(true);
  const [configured, setConfigured] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);

  // Alert URL lookup: product_id → { catalogUrl, cheapestUrl }


  // Compare modal
  const [compareProduct, setCompareProduct] = useState<CatalogProduct | null>(null);
  const [compareData, setCompareData] = useState<CompareData | null>(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const [compareError, setCompareError] = useState(false);

  // Product filters
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [categoryFilter] = useState("");
  const [availabilityFilter, setAvailabilityFilter] = useState("");
  const [sort, setSort] = useState("name");

  useEffect(() => {
    loadAll();
  }, [currentTenant.id]);

  useEffect(() => {
    loadProducts();
  }, [currentTenant.id, page, search, categoryFilter, availabilityFilter, sort]);

  const loadAll = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const [intelRes, alertsRes, compRes] = await Promise.all([
        tenantFetch("/api/v1/catalog/intelligence"),
        tenantFetch("/api/v1/catalog/alerts"),
        tenantFetch("/api/v1/catalog/competitors"),
      ]);

      if (intelRes.status === 404) {
        setConfigured(false);
        return;
      }

      if (!intelRes.ok) {
        setLoadError(true);
        return;
      }

      if (intelRes.ok) setIntelligence((await intelRes.json()).data);
      if (compRes.ok) setCompetitors((await compRes.json()).data ?? []);

      const alertList: CatalogAlert[] = alertsRes.ok ? ((await alertsRes.json()).data ?? []) : [];
      setAlerts(alertList);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    setProductsLoading(true);
    const params = new URLSearchParams({ page: String(page), limit: "20", sort });
    if (search) params.set("q", search);
    if (categoryFilter) params.set("category", categoryFilter);
    if (availabilityFilter) params.set("availability", availabilityFilter);
    try {
      const res = await tenantFetch(`/api/v1/catalog/products?${params}`);
      if (res.ok) setProducts((await res.json()).data);
    } catch { /* ignore */ } finally {
      setProductsLoading(false);
    }
  };


  const openCompare = async (product: CatalogProduct) => {
    setCompareProduct(product);
    setCompareData(null);
    setCompareError(false);
    setCompareLoading(true);
    try {
      const res = await tenantFetch(`/api/v1/catalog/${product.id}/compare`);
      if (res.ok) {
        setCompareData((await res.json()).data);
      } else {
        setCompareError(true);
      }
    } catch {
      setCompareError(true);
    } finally {
      setCompareLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <Skeleton width={280} height={32} />
          <Skeleton width={320} height={16} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => <Skeleton key={i} width="100%" height={96} />)}
        </div>
        <Skeleton width="100%" height={200} />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => <Skeleton key={i} width="100%" height={220} />)}
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="space-y-6">
        <div className="space-y-1">
          <h1 className="text-[29px] font-light text-foreground">Market Intelligence</h1>
          <p className="text-[14px] font-light text-muted-foreground">Competitive pricing for your catalog</p>
        </div>
        <div className="max-w-lg">
          <Card variant="outlined" padding="none">
            <CardContent>
              <EmptyState
                title="Failed to load"
                description="There was a problem fetching your market intelligence data."
                action={
                  <Button variant="primary" onClick={loadAll}>Try again</Button>
                }
              />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!configured) {
    return (
      <FeatureGate
        icon={
          <img src="/images/marketplaces/mi-icon.svg" alt="" width={64} height={64} />
        }
        title="Catalog not yet configured"
        description="Your product catalog hasn't been connected yet. Contact Pentatonic to get set up with competitive pricing intelligence."
        action={
          <Button variant="primary" onClick={() => { window.location.href = "mailto:hello@pentatonic.com?subject=Catalog%20pricing%20setup"; }}>
            Get in touch
          </Button>
        }
      />
    );
  }


  const formatDate = (iso: string) => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  const availabilityVariant = (a: string): "success" | "warning" | "error" | "default" =>
    a === "in_stock" ? "success" : a === "pre_order" || a === "eta" ? "warning" : a === "unavailable" ? "error" : "default";

  // Competitive Health Score: 0–100 index weighted by price position
  // Cheapest = 1.0pt, competitive (within 5%) = 0.5pt, most expensive = 0pt
  const healthScore = (() => {
    const pos = intelligence?.price_position;
    if (!pos || pos.products_analysed === 0) return null;
    const raw = (pos.catalog_cheapest * 1.0 + pos.catalog_competitive * 0.5) / pos.products_analysed * 100;
    return Math.round(Math.min(100, raw));
  })();
  const healthScoreColor = healthScore === null ? "text-muted-foreground"
    : healthScore >= 70 ? "text-[#3941EB]"
    : healthScore >= 40 ? "text-foreground"
    : "text-[#FF4C00]";
  const healthScoreLabel = healthScore === null ? "No data yet"
    : healthScore >= 70 ? "Strong competitive position"
    : healthScore >= 40 ? "Room to improve"
    : "Significant pricing pressure";
  const avgGapColor = !intelligence?.price_position ? "text-foreground"
    : intelligence.price_position.avg_gap_pct > 0 ? "text-[#FF4C00]"
    : "text-[#3941EB]";

  // Pagination logic (same as brand view)
  const totalPages = products?.pages ?? 1;
  const getPageNumbers = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages: (number | "ellipsis")[] = [1];
    if (page > 3) pages.push("ellipsis");
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pages.push(i);
    if (page < totalPages - 2) pages.push("ellipsis");
    pages.push(totalPages);
    return pages;
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="space-y-1">
        <h1 className="text-[29px] font-light text-foreground leading-[1.1]">Market Intelligence</h1>
        <p className="text-[14px] font-light text-muted-foreground">Competitive pricing powered by real-time market data</p>
      </div>

      {/* KPI strip */}
      {intelligence && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card variant="outlined" padding="md">
            <CardContent>
              <p className="font-mono text-[14px] uppercase tracking-[0.02em] text-foreground leading-[1.1] mb-2">Health Score</p>
              <p className={`text-[47px] font-thin leading-none ${healthScoreColor}`}>
                {healthScore !== null ? healthScore : "—"}
              </p>
              <p className="font-sans font-light text-[14px] leading-[1.4] text-grey-medium mt-2">{healthScoreLabel}</p>
            </CardContent>
          </Card>
          <Card variant="outlined" padding="md">
            <CardContent>
              <p className="font-mono text-[14px] uppercase tracking-[0.02em] text-foreground leading-[1.1] mb-2">Total Products</p>
              <p className="text-[47px] font-thin text-foreground leading-none">{intelligence.catalog.total_products.toLocaleString()}</p>
              <p className="font-sans font-light text-[14px] leading-[1.4] text-grey-medium mt-2">In your catalog</p>
            </CardContent>
          </Card>
          <Card variant="outlined" padding="md">
            <CardContent>
              <p className="font-mono text-[14px] uppercase tracking-[0.02em] text-foreground leading-[1.1] mb-2">Products Analysed</p>
              <p className="text-[47px] font-thin text-foreground leading-none">{intelligence.price_position?.products_analysed ?? 0}</p>
              <p className="font-sans font-light text-[14px] leading-[1.4] text-grey-medium mt-2">With competitor pricing data</p>
            </CardContent>
          </Card>
          <Card variant="outlined" padding="md">
            <CardContent>
              <p className="font-mono text-[14px] uppercase tracking-[0.02em] text-foreground leading-[1.1] mb-2">Avg Price Gap</p>
              <p className={`text-[47px] font-thin leading-none ${avgGapColor}`}>
                {intelligence.price_position
                  ? `${intelligence.price_position.avg_gap_pct > 0 ? "+" : ""}${intelligence.price_position.avg_gap_pct.toFixed(1)}%`
                  : "—"}
              </p>
              <p className="font-sans font-light text-[14px] leading-[1.4] text-grey-medium mt-2">
                {intelligence.price_position?.avg_gap_pct > 0 ? "Above market average" : "Below market average"}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs: Alerts · Catalog · Competitors */}
      <Tabs defaultValue="alerts">
        <TabsList>
          <TabsTrigger value="alerts">
            <span className="flex items-center gap-2">Alerts{alerts.length > 0 && <Badge variant="error" size="sm">{alerts.length}</Badge>}</span>
          </TabsTrigger>
          <TabsTrigger value="catalog">Catalog</TabsTrigger>
          <TabsTrigger value="competitors">Competitors</TabsTrigger>
        </TabsList>

        {/* Alerts tab */}
        <TabsContent value="alerts">
          <div className="space-y-4 pt-4">
            <p className="text-[18px] font-light text-muted-foreground">{alerts.length > 0 ? `${alerts.length} product${alerts.length === 1 ? "" : "s"} where a competitor is undercutting you` : "No competitors are currently undercutting you"}</p>
            {alerts.length === 0 ? (
              <Card variant="outlined" padding="none">
                <CardContent>
                  <EmptyState
                    title="No price alerts"
                    description="You're not being undercut on any matched products right now."
                  />
                </CardContent>
              </Card>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead>Your price</TableHead>
                    <TableHead>Lowest price</TableHead>
                    <TableHead>Difference</TableHead>
                    <TableHead>Competitor</TableHead>
                    <TableHead>Last seen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {alerts.map((alert) => (
                    <TableRow
                      key={alert.product_id}
                      onClick={() => openCompare({
                        id: alert.product_id,
                        name: alert.product_name,
                        brand: "",
                        price: alert.catalog_price,
                        currency: "GBP",
                        category: "",
                        availability: "in_stock",
                        url: alert.catalog_url,
                        image_url: "",
                        sku: "",
                        price_position: {
                          is_cheapest: false,
                          gap_pct: alert.price_gap_pct,
                          cheapest_competitor_name: alert.cheapest_competitor_name,
                          cheapest_competitor_price: alert.cheapest_price,
                        },
                      })}
                      style={{ cursor: "pointer" }}
                    >
                      <TableCell>
                        <span className="block max-w-[280px] truncate">{alert.product_name}</span>
                      </TableCell>
                      <TableCell>{formatCcy(alert.catalog_price)}</TableCell>
                      <TableCell>{formatCcy(alert.cheapest_price)}</TableCell>
                      <TableCell>
                        <span className="text-[#FF4C00] font-light">+{alert.price_gap_pct.toFixed(1)}%</span>
                      </TableCell>
                      <TableCell>{alert.cheapest_competitor_name}</TableCell>
                      <TableCell>{alert.price_scraped_at ? formatDate(alert.price_scraped_at) : "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>

        {/* Catalog tab */}
        <TabsContent value="catalog">
          <div className="space-y-6 pt-4">
            {/* Filter toggle */}
            {(() => {
              const activeCount = [search, availabilityFilter, sort !== "name" ? sort : ""].filter(Boolean).length;
              return (
                <div className="space-y-3">
                  <div className="flex items-center justify-between gap-4">
                    <p className="text-[18px] font-light text-muted-foreground">Browse your catalog and click any product to compare competitor pricing</p>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <Toggle
                        variant="outline"
                        size="md"
                        pressed={showFilters}
                        onPressedChange={setShowFilters}
                      >
                        <span className="flex items-center gap-2">
                          <Filter size={14} weight="LineDuotone" />
                          {showFilters ? "Hide filters" : "Filters"}{activeCount > 0 && ` (${activeCount})`}
                        </span>
                      </Toggle>
                      {activeCount > 0 && (
                        <button
                          className="text-[13px] font-light text-muted-foreground hover:text-foreground transition-colors"
                          onClick={() => { setSearch(""); setAvailabilityFilter(""); setSort("name"); setPage(1); }}
                        >
                          Clear all
                        </button>
                      )}
                    </div>
                  </div>

                  {showFilters && (
                    <div className="flex flex-wrap gap-3 items-end">
                      <div className="w-64">
                        <Input
                          label="Search products"
                          placeholder="e.g. Running shoes"
                          value={search}
                          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                        />
                      </div>
                      <div className="w-44">
                        <Select value={availabilityFilter} onValueChange={(v: string) => { setAvailabilityFilter(v === "all" ? "" : v); setPage(1); }}>
                          <SelectTrigger label="Availability">
                            <SelectValue placeholder="All" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All</SelectItem>
                            <SelectItem value="in_stock">In stock</SelectItem>
                            <SelectItem value="pre_order">Pre-order</SelectItem>
                            <SelectItem value="unavailable">Unavailable</SelectItem>
                            <SelectItem value="eta">ETA</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="w-44">
                        <Select value={sort} onValueChange={setSort}>
                          <SelectTrigger label="Sort">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="name">Name A–Z</SelectItem>
                            <SelectItem value="price_asc">Price: low to high</SelectItem>
                            <SelectItem value="price_desc">Price: high to low</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Product grid */}
            {productsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Spinner size="md" />
              </div>
            ) : !products || products.items.length === 0 ? (
              <Card variant="outlined" padding="none">
                <CardContent>
                  <EmptyState icon={<Magnifer size={32} weight="LineDuotone" />} title="No products found" description="Try adjusting your filters or search terms." />
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
                {products.items.map((product) => (
                  <Card key={product.id} variant="outlined" padding="none" asChild>
                    <button onClick={() => openCompare(product)} className="text-left flex flex-col w-full cursor-pointer">
                      <AspectRatio ratio={1}>
                        {product.image_url ? (
                          <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-muted">
                            <span className="text-muted-foreground text-[14px] font-mono uppercase">No image</span>
                          </div>
                        )}
                      </AspectRatio>
                      <div className="p-3 flex flex-col gap-2 flex-1">
                        <div>
                          <p className="font-sans font-light text-[14px] leading-[1.4] text-grey-medium mb-1">{product.brand}</p>
                          <p className="text-[13px] font-light text-foreground leading-[1.3] line-clamp-2 overflow-hidden">{product.name}</p>
                        </div>
                        <div className="mt-auto space-y-1.5">
                          <div className="flex items-center justify-between">
                            <p className="text-[15px] font-light text-foreground">{formatCcy(product.price, product.currency)}</p>
                            <Badge variant={availabilityVariant(product.availability)} size="sm">
                              {product.availability.replace(/_/g, " ")}
                            </Badge>
                          </div>
                          {product.price_position && (
                            product.price_position.is_cheapest
                              ? <p className="text-[11px] font-light text-green-600">Lowest price</p>
                              : <p className="text-[11px] font-light text-muted-foreground">
                                  {product.price_position.cheapest_competitor_name} {formatCcy(product.price_position.cheapest_competitor_price, product.currency)}
                                </p>
                          )}
                        </div>
                      </div>
                    </button>
                  </Card>
                ))}
              </div>
            )}

            {/* Pagination */}
            {products && products.pages > 1 && (
              <>
                <Separator />
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious onClick={() => setPage(p => Math.max(1, p - 1))} />
                    </PaginationItem>
                    {getPageNumbers().map((p, i) =>
                      p === "ellipsis" ? (
                        <PaginationItem key={`ellipsis-${i}`}><PaginationEllipsis /></PaginationItem>
                      ) : (
                        <PaginationItem key={p}>
                          <PaginationLink isActive={page === p} onClick={() => setPage(p as number)}>{p}</PaginationLink>
                        </PaginationItem>
                      )
                    )}
                    <PaginationItem>
                      <PaginationNext onClick={() => setPage(p => Math.min(totalPages, p + 1))} />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </>
            )}
          </div>
        </TabsContent>

        {/* Competitors tab */}
        <TabsContent value="competitors">
          <div className="space-y-4 pt-4">
            {competitors.length === 0 ? (
              <Card variant="outlined" padding="none">
                <CardContent>
                  <EmptyState
                    icon={<Buildings2 size={32} weight="LineDuotone" />}
                    title="No competitors yet"
                    description="Competitor sites are discovered automatically as your catalog is analysed. Check back after your first scan completes."
                  />
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {competitors.map((comp) => (
                  <Card key={comp.domain} variant="outlined" padding="md">
                    <CardContent>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <img
                              src={`https://www.google.com/s2/favicons?domain=${comp.domain}&sz=32`}
                              alt=""
                              width={20}
                              height={20}
                              className="flex-shrink-0"
                            />
                            <p className="text-[18px] font-light text-foreground truncate">{comp.name}</p>
                          </div>
                          <a
                            href={`https://${comp.domain}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={`Visit ${comp.name}`}
                            className="flex items-center gap-1 text-[14px] font-light text-muted-foreground hover:text-foreground transition-colors flex-shrink-0 whitespace-nowrap"
                          >
                            Visit site <ArrowRightUp size={14} weight="LineDuotone" />
                          </a>
                        </div>
                        <div className="flex gap-4 text-[14px] font-light">
                          <span className="text-muted-foreground">{comp.products_scraped} products found</span>
                          <span className="text-foreground font-light">{comp.products_matched} matched to your catalog</span>
                        </div>
                        <p className="font-sans font-light text-[14px] leading-[1.4] text-grey-medium">Last scanned {formatDate(comp.last_scraped)}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Compare Sheet */}
      <Sheet open={!!compareProduct} onOpenChange={(open) => { if (!open) { setCompareProduct(null); setCompareData(null); setCompareError(false); } }}>
        <SheetContent side="right">
          <SheetHeader>
            <SheetTitle>Price comparison</SheetTitle>
            <SheetDescription>{compareProduct?.name ?? ""}</SheetDescription>
          </SheetHeader>

          <SheetBody>
            <div className="flex flex-col gap-6">
              {/* Catalog product */}
              {compareProduct && (
                <Card variant="outlined" padding="sm">
                  <CardContent>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex flex-col gap-2">
                        <p className="text-[14px] font-mono uppercase tracking-[0.02em] text-muted-foreground">Your listing</p>
                        <p className="text-[18px] font-light text-foreground">{formatCcy(compareProduct.price, compareProduct.currency)}</p>
                      </div>
                      {compareProduct.url && (
                        <a
                          href={compareProduct.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-[14px] font-light text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap"
                          onClick={(e) => e.stopPropagation()}
                        >
                          View listing <ArrowRightUp size={14} weight="LineDuotone" />
                        </a>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Competitor prices */}
              {compareError ? (
                <div className="flex flex-col gap-4">
                  <Alert variant="error">
                    <AlertTitle>Failed to load prices</AlertTitle>
                    <AlertDescription>Something went wrong fetching competitor data.</AlertDescription>
                  </Alert>
                  <Button variant="secondary" size="sm" onClick={() => compareProduct && openCompare(compareProduct)}>
                    Try again
                  </Button>
                </div>
              ) : compareLoading ? (
                <div className="flex justify-center py-8"><Spinner /></div>
              ) : compareData && compareData.competitor_prices.length > 0 ? (
                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-2">
                    <p className="text-[14px] font-mono uppercase tracking-[0.02em] text-muted-foreground">Competitor matches</p>
                    <Badge variant="default" size="sm">{compareData.competitor_prices.length}</Badge>
                  </div>
                  <div className="flex flex-col gap-2">
                    {compareData.competitor_prices.map((comp) => (
                      <Card key={comp.competitor_domain} variant="outlined" padding="sm">
                        <CardContent>
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex flex-col gap-2 flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <img
                                  src={`https://www.google.com/s2/favicons?domain=${comp.competitor_domain}&sz=32`}
                                  alt=""
                                  width={16}
                                  height={16}
                                  className="flex-shrink-0"
                                />
                                <span className="text-[14px] font-light text-foreground">{comp.competitor_name}</span>
                                <Badge variant={availabilityVariant(comp.availability)} size="sm">
                                  {AVAILABILITY_LABELS[comp.availability] ?? comp.availability.replace(/_/g, " ")}
                                </Badge>
                              </div>
                              <p className="text-[14px] font-mono uppercase tracking-[0.02em] text-muted-foreground truncate">{comp.competitor_domain}</p>
                              <p className="text-[14px] font-light text-muted-foreground line-clamp-1">{comp.product_name}</p>
                              <div className="flex items-baseline gap-2">
                                <p className="text-[18px] font-light text-foreground">{formatCcy(comp.price, comp.currency)}</p>
                                <span className={`text-[14px] font-light ${comp.price_gap_pct > 0 ? "text-[#FF4C00]" : "text-[#3941EB]"}`}>
                                  {comp.price_gap_pct > 0
                                    ? `${comp.price_gap_pct.toFixed(1)}% cheaper`
                                    : `${Math.abs(comp.price_gap_pct).toFixed(1)}% pricier`}
                                </span>
                              </div>
                            </div>
                            <a
                              href={comp.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-[14px] font-light text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap flex-shrink-0"
                            >
                              View listing <ArrowRightUp size={14} weight="LineDuotone" />
                            </a>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              ) : compareData ? (
                <EmptyState
                  icon={<ChartSquare size={48} weight="LineDuotone" />}
                  title="No competitor matches found"
                  description="No matches found yet. Scans run daily — check back tomorrow."
                />
              ) : null}
            </div>
          </SheetBody>
        </SheetContent>
      </Sheet>
    </div>
  );
}

// --- Subcomponents ---

function MetricCard({
  label,
  value,
  subtext,
  footnote,
  large,
}: {
  label: string;
  value: string;
  subtext: string;
  footnote?: string;
  large?: boolean;
}) {
  // Split trailing compact suffix (K, M, B, %) to render at smaller size
  const match = value.match(/^(.*?)([KMB%]?)$/);
  const main = match?.[1] ?? value;
  const suffix = match?.[2] ?? "";

  return (
    <div className={`bg-white border border-grey flex flex-col gap-4 p-6 flex-1 min-w-0 ${large ? "min-h-[220px]" : ""}`}>
      <p className="font-mono text-[14px] leading-[1.1] uppercase tracking-[0.02em] text-foreground">
        {label}
      </p>
      <p className={`font-sans font-light leading-[1] text-foreground ${large ? "text-[96px]" : "text-[47px]"}`}>
        {suffix ? (
          <>
            {main}
            <span className={large ? "text-[56px]" : "text-[29px] leading-[1.4]"}>{suffix}</span>
          </>
        ) : (
          value
        )}
      </p>
      <p className="font-sans font-light text-[14px] leading-[1.4] text-grey-medium">
        {subtext}
      </p>
      {footnote && (
        <p className="font-mono text-[14px] leading-[1.4] text-grey-medium mt-1">
          * {footnote}
        </p>
      )}
    </div>
  );
}


function OpportunityCard({
  label,
  value,
  description,
}: {
  label: string;
  value: string;
  description: string;
}) {
  // Split leading currency prefix and trailing compact suffix (K, M, B, %)
  const match = value.match(/^(£?)(.+?)([KMB%]?)$/);
  const prefix = match?.[1] ?? "";
  const main = match?.[2] ?? value;
  const suffix = match?.[3] ?? "";

  return (
    <div className="bg-white border border-grey p-6 flex flex-col gap-4">
      {/* Label */}
      <p className="font-mono text-[14px] leading-[1.1] uppercase tracking-[0.02em] text-grey-medium">
        {label}
      </p>

      {/* Value */}
      <p className="font-sans font-thin text-[92px] leading-none text-foreground">
        {prefix && <span>{prefix}</span>}
        {main}
        {suffix && <span className="text-[56px]">{suffix}</span>}
      </p>

      {/* Description */}
      <p className="font-sans font-light text-[18px] leading-[1.4] text-foreground">
        {description}
      </p>
    </div>
  );
}


// --- Helpers ---

function formatCompact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return n.toFixed(0);
}

function calculateTakebackOpportunity(annualTransactions: number, avgPrice: number): number {
  const resaleMargin = avgPrice * 0.35;
  const newPurchaseContribution = (avgPrice * 0.5 + 35) * 0.85 * 0.60;
  const perTransactionValue = resaleMargin + newPurchaseContribution;
  return Math.round(annualTransactions * 0.15 * perTransactionValue);
}


function formatCcy(price: number, currency = "GBP"): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency, minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(price);
}

function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "2-digit",
    });
  } catch {
    return dateStr;
  }
}

// --- Demo data fallbacks ---

function getDemoIntelligence(
  tenantId: string,
  period: string,
): IntelligenceData {
  const days = period === "7d" ? 7 : period === "90d" ? 90 : 30;
  const brandName = tenantId.charAt(0).toUpperCase() + tenantId.slice(1);
  const baseListings =
    tenantId === "nike"
      ? 185000
      : tenantId === "adidas"
        ? 142000
        : tenantId === "arcteryx"
          ? 8500
          : 25000;
  const baseSold = Math.round(baseListings * 0.12 * (days / 30));
  const avgPrice =
    tenantId === "arcteryx"
      ? 145
      : tenantId === "nike"
        ? 62
        : tenantId === "adidas"
          ? 55
          : 85;

  const daily_trend = [];
  for (let i = days; i >= 0; i--) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    daily_trend.push({
      date: date.toISOString().split("T")[0],
      active_listings: baseListings + Math.floor(Math.random() * 2000 - 1000),
      sales: Math.floor(baseSold / days + Math.random() * 20 - 10),
      avg_price: avgPrice + Math.floor(Math.random() * 20 - 10),
    });
  }

  return {
    brand_id: tenantId,
    brand_name: brandName,
    period_days: days,
    total_active_listings: baseListings,
    total_sold_period: baseSold,
    estimated_annual_gmv: baseSold * avgPrice * (365 / days),
    avg_sale_price: avgPrice,
    sell_through_rate: 12.4,
    avg_rrp_percentage:
      tenantId === "arcteryx" ? 62 : tenantId === "nike" ? 48 : 45,
    platform_breakdown: {
      ebay: Math.round(baseListings * 0.65),
      vinted: Math.round(baseListings * 0.18),
      depop: Math.round(baseListings * 0.08),
      therealreal: Math.round(baseListings * 0.05),
      vestiaire: Math.round(baseListings * 0.04),
    },
    condition_breakdown: {
      "New with tags": Math.round(baseListings * 0.15),
      "Like New": Math.round(baseListings * 0.22),
      Used: Math.round(baseListings * 0.35),
      Good: Math.round(baseListings * 0.2),
      Fair: Math.round(baseListings * 0.08),
    },
    category_breakdown: {
      Shoes: Math.round(baseListings * 0.35),
      "Jackets & Coats": Math.round(baseListings * 0.2),
      Tops: Math.round(baseListings * 0.15),
      Pants: Math.round(baseListings * 0.12),
      Accessories: Math.round(baseListings * 0.1),
      Bags: Math.round(baseListings * 0.08),
    },
    daily_trend,
  };
}

function getDemoListings(tenantId: string): ListingsResponse {
  const brandName = tenantId.charAt(0).toUpperCase() + tenantId.slice(1);
  const platforms = ["ebay", "vinted", "depop", "therealreal"];
  const conditions = ["New with tags", "Like New", "Used", "Good"];
  const items: ListingItem[] = [];

  for (let i = 0; i < 15; i++) {
    const platform = platforms[i % platforms.length];
    items.push({
      id: `demo-${i}`,
      title: `${brandName} ${["Running Shoe", "Jacket", "T-Shirt", "Hoodie", "Shorts", "Backpack", "Cap", "Sneaker", "Pants", "Vest", "Polo", "Slides", "Tracksuit", "Beanie", "Socks"][i]} - Size ${["S", "M", "L", "XL", "10", "11", "42", "44"][i % 8]}`,
      price: 25 + Math.floor(Math.random() * 150),
      price_gbp: 25 + Math.floor(Math.random() * 150),
      currency: "GBP",
      platform,
      condition: conditions[i % conditions.length],
      listing_type: "active",
      listing_url: "",
      image_urls: [],
      category: ["Shoes", "Jackets", "Tops", "Bottoms", "Accessories"][i % 5],
      size: ["S", "M", "L", "XL", "10"][i % 5],
      first_seen_at: new Date(
        Date.now() - Math.random() * 30 * 86400000,
      ).toISOString(),
      last_seen_at: new Date().toISOString(),
    });
  }

  return { items, total: 1500, page: 1, pages: 100 };
}
